<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { useFonts } from '@/composables/fonts';
import FontSelector from '../FontSelector.vue';
import CInput from '@/components/shared/CInput.vue';
import type { CSelectItem } from '@/components/shared/CSelect';
import type { ImagePlaceholderElement, WordCloudConfig } from '@/typings/customilyElements/canvasElements';
import type { CanvasEx } from '@/typings/canvas';
import { ptToPx, pxToPt } from '@/utils/elementsUtils';

const props = defineProps<{
    canvas: CanvasEx;
    imageElement?: ImagePlaceholderElement;
    editMode?: boolean;
}>();

const model = reactive({
    font: undefined as CSelectItem<string> | undefined,
    minFontSize: 0,
    maxFontSize: 50,
    maxWords: 100
});

const canvasDpi = computed(() => props.canvas.DPI);

const { getFontFromPath, generateFontName } = useFonts();
watch(
    () => props.imageElement,
    (element) => {
        if (!element || !element.wordCloudConfig) return;

        const config = element.wordCloudConfig;
        model.minFontSize = pxToPt(config.minFontSize || 0, canvasDpi.value);
        model.maxFontSize = pxToPt(config.maxFontSize || 50, canvasDpi.value);
        model.maxWords = config.maxWords || 100;

        const font = getFontFromPath(config.font);
        if (font) {
            model.font = {
                label: generateFontName(font),
                value: config.font
            };
        }
    },
    { immediate: true }
);

const disabled = computed(() => !!props.imageElement?.locked);

const onFontSelected = () => {
    updateWordCloudConfig({ font: model.font?.value });
};

const updateFontSize = (prop: 'minFontSize' | 'maxFontSize', value = 0) => {
    updateWordCloudConfig({ [prop]: ptToPx(value, canvasDpi.value) });
};

const updateWordCloudConfig = (config: Partial<WordCloudConfig>) => {
    const element = props.imageElement;
    if (!element) return;

    const newConfig = { ...element.wordCloudConfig, ...config } as WordCloudConfig;
    element.set('wordCloudConfig', newConfig);
    if (props.editMode) element.setEditedProp('wordCloudConfig');
};
</script>

<template>
    <div class="word-cloud-character-wrapper">
        <div class="row">
            <font-selector v-model="model.font" class="col-12" :disabled="disabled" @input="onFontSelected" />
            <div class="col-8 d-flex align-items-center">
                <c-input
                    v-model="model.minFontSize"
                    class="flex-fill"
                    type="decimal"
                    step=".01"
                    placeholder="0.00"
                    :min="0"
                    label="Min size(pts)"
                    :disabled="disabled"
                    @input="updateFontSize('minFontSize', model.minFontSize)"
                />
                <span class="mx-2"> to </span>
                <c-input
                    v-model="model.maxFontSize"
                    type="decimal"
                    step=".01"
                    :min="0"
                    placeholder="0.00"
                    class="flex-fill"
                    label="Max size(pts)"
                    :disabled="disabled"
                    @input="updateFontSize('maxFontSize', model.maxFontSize)"
                />
            </div>
            <div class="col-4">
                <c-input
                    v-model="model.maxWords"
                    type="number"
                    :min="0"
                    :max="200"
                    placeholder="0.00"
                    class="flex-fill"
                    label="Max Words"
                    :disabled="disabled"
                    @input="updateWordCloudConfig({ maxWords: model.maxWords })"
                />
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.word-cloud-character-wrapper {
    .row {
        row-gap: 0.625rem;
    }
}
</style>
